import React, { useEffect, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import { useAdminApi } from "../api/AdminApi";
import { useFontenehusApi } from "../api/FontenehusApi";
import { useNavigate } from "react-router-dom";
import { useFontenehus } from "../context/FontenehusContext";
import { toast } from "react-toastify";
import { handleApiError } from "../utils/handleApiError";
import { useUser } from "../context/UserContext";
import ActivityLog from "../components/AdminDashboard/ActivityLog";

const AdminDashboard: React.FC = () => {
  usePageTitle("Admin – FonteneLunsj");
  const { getAllUsersByFontenehusId } = useAdminApi();
  const { getAllFontenehus, createFontenehus } = useFontenehusApi();
  const [users, setUsers] = useState<any[]>([]);
  const { user } = useUser();
  const [fontenehusList, setFontenehusList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newFontenehusName, setNewFontenehusName] = useState("");
  const [showFontenehus, setShowFontenehus] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const { selectedFontenehus } = useFontenehus();
  const isSuperAdmin = user?.roles?.some((r) => ["SuperAdmin"].includes(r));
  const navigate = useNavigate();
  const isOwnFontenehus = (fontenehusId: number) =>
    user?.fontenehusId == fontenehusId;
  // Fetch users whenever selectedFontenehus changes

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const userResult = await getAllUsersByFontenehusId(
          selectedFontenehus?.id,
        );

        setUsers(userResult.data.data);
      } catch (err: any) {
        setUsers([]);
        handleApiError(err, "Feil ved lasting av brukere.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedFontenehus]);

  // Fetch fontenehus list only (for dropdown)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [f] = await Promise.all([getAllFontenehus()]);
        setFontenehusList(f.data.data);
      } catch (err) {
        handleApiError(err, "Feil ved lasting av fontenehus.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleAddFontenehus = async () => {
    if (!newFontenehusName.trim()) return;

    try {
      const fh = await createFontenehus(newFontenehusName);
      setFontenehusList((prev) => [...prev, fh.data.data]);
      setNewFontenehusName("");
      toast.success("Opprettet Fontenehus: " + newFontenehusName);
    } catch (err: any) {
      handleApiError(err, "Feil ved oppretting av fontenehus");
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      (u.username.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.displayName.toLowerCase().includes(userSearch.toLowerCase())) &&
      (!u.username.toLowerCase().includes("anonym") ||
        u.roles?.some((r: string) => ["User", "Guest"].includes(r))),
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full pt-[80px]">
        <p className="text-orangeBrand text-lg font-semibold">
          Laster admin-panel...
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-orange-50 pt-[80px] pb-[80px] overflow-y-auto">
      <main className="flex flex-col gap-8 max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-orangeBrand text-center mb-4">
          Admin Panelet
        </h1>

        {/* ✅ USER MANAGEMENT */}
        <section className="bg-white shadow p-5 rounded-xl">
          <h2 className="text-xl font-semibold text-orangeBrand mb-3">
            Brukerroller
          </h2>

          <input
            type="text"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Søk etter bruker..."
            className="mb-4 w-full border rounded px-3 py-2"
          />

          <table className="w-full text-left">
            <thead>
              <tr className="text-gray-600 border-b">
                <th className="py-2">Brukernavn</th>
                <th className="py-2">Visningsnavn</th>
                <th className="py-2">Roller</th>
                <th>Handling</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-b">
                  <td className="py-2 font-medium">{u.username}</td>
                  <td className="py-2 font-medium">{u.displayName}</td>
                  {/* ✅ Display roles */}
                  <td className="py-2 text-gray-700">
                    {Array.isArray(u.roles)
                      ? u.roles.join(", ")
                      : u.role || "Ingen rolle"}
                  </td>
                  <td className="py-2">
                    <button
                      onClick={() => navigate(`/admin/user/${u.id}`)}
                      className="bg-orangeBrand text-white px-3 py-1 rounded hover:bg-orangeHover"
                    >
                      Administrer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <>
          {/* ✅ FONTENEHUS MANAGEMENT (DROPDOWN) */}
          <section className="bg-white shadow p-5 rounded-xl">
            <button
              onClick={() => {
                if (fontenehusList.length === 0) {
                }
                setShowFontenehus((s) => !s);
              }}
              className="w-full flex justify-between items-center text-xl font-semibold text-orangeBrand"
            >
              <span>Fontenehus</span>
              <span className="text-gray-500">
                {showFontenehus ? "▲" : "▼"}
              </span>
            </button>

            {/* ✅ Animated dropdown container */}
            <div
              className={`transition-all duration-300 overflow-hidden ${
                showFontenehus ? "max-h-[500px] mt-4" : "max-h-0"
              }`}
            >
              {/* Add new fontenehus */}
              {isSuperAdmin && (
                <div className="mb-4 flex gap-2">
                  <input
                    value={newFontenehusName}
                    onChange={(e) => setNewFontenehusName(e.target.value)}
                    placeholder="Nytt Fontenehus-navn"
                    className="flex-1 border rounded px-3 py-2"
                  />
                  <button
                    onClick={handleAddFontenehus}
                    className="bg-orangeBrand text-white px-4 py-2 rounded hover:bg-orangeHover"
                  >
                    Legg til
                  </button>
                </div>
              )}
              {/* List of houses */}
              <div className="overflow-x-auto">
                <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 font-semibold text-gray-700 border-b border-gray-300 pb-2 mb-2">
                  <span>Navn</span>
                  <span>Kode</span>
                  <span>Handling</span>
                </div>

                <ul className="flex flex-col gap-2">
                  {fontenehusList.map((fh) =>
                    isOwnFontenehus(fh.id) || isSuperAdmin ? ( // replace `true` with your condition
                      <li
                        key={fh.id}
                        className="grid grid-cols-[2fr_1fr_1fr] gap-2 items-center bg-orange-50 p-3 rounded-md shadow-sm"
                      >
                        <span>{fh.name}</span>
                        <span className="text-gray-500">
                          {fh.fontenehusCode}
                        </span>
                        <button
                          onClick={() => navigate(`/admin/fontenehus/${fh.id}`)}
                          className="bg-orangeBrand text-white px-3 py-1 rounded hover:bg-orangeHover"
                        >
                          Administrer
                        </button>
                      </li>
                    ) : null,
                  )}
                </ul>
              </div>
            </div>
          </section>
        </>

        <ActivityLog />
      </main>
    </div>
  );
};

export default AdminDashboard;
